import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "ora";
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  return `${Math.floor(s / 86400)} g fa`;
};

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
    if (n.link) nav(n.link);
  };

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative p-2 text-neutral-300 hover:text-[#D4AF37] transition-colors" data-testid="notification-bell-button" aria-label="Notifiche">
          <Bell className="w-5 h-5" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#D4AF37] text-[#0B0C0E] text-[10px] font-bold flex items-center justify-center rounded-full" data-testid="notification-unread-count">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0 bg-[#121418]/95 backdrop-blur-xl border-white/10 rounded-sm" data-testid="notification-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <span className="eyebrow">Notifiche</span>
          {data.unread > 0 && <button onClick={readAll} className="text-[11px] text-neutral-400 hover:text-[#D4AF37]" data-testid="notification-read-all-button">Segna tutte come lette</button>}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {data.items.length === 0 ? (
            <p className="px-5 py-10 text-sm text-neutral-500 text-center" data-testid="notification-empty">Nessuna notifica per ora</p>
          ) : data.items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-5 py-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors flex gap-3 ${n.read ? "opacity-60" : ""}`} data-testid={`notification-item-${n.id}`}>
              <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-[#D4AF37]"}`} />
              <span>
                <span className="block text-sm text-neutral-200 leading-snug">{n.message}</span>
                <span className="block text-[11px] text-neutral-500 mt-1 font-mono">{timeAgo(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
